/**
 * @typedef {'base'|'text'|'radio'|'int'|'float'|'address'|'date'|'time'|'textarea'|'image'|'products'|'order'|'checkbox'|'store-status'} FieldType
 */
/**
 * @typedef {number|string|Array} FieldValueType
 */
/**
 * @typedef {"en_us"|"zh_cn"} Language
 */
/**
 * @typedef {Object} FormatOptions
 * @property {Array<{label: string, value: any}>} [options]
 * @property {number} [maximum]
 * @property {number} [minimum]
 * @property {boolean} [editable]
 * @property ...
 */

/**
 * @typedef {Object} BaseComposerProps
 * @property {string} name
 * name of field.
 * @property {FieldType} type
 * type of value of this field.
 * @property {FieldValueType} value
 * a read-only variable, recommended to create it from useState().
 * For example,
 * 'const [value, setValue] = useState(initValue)' .
 * @property {function(FieldValueType): Promise} setValue.
 * the function of set value, same recommendation with variable 'value'.
 * @property {FormatOptions} [format]
 * some format of input component, like maximum and minimum for integer component
 * @property {string} [className]
 * the customized class name of input component, using it for set CSS style.
 * @property {string} [description]
 * the description of the field
 * @property {JSX.Element} children
 * only works at some input component. Working at ['base', ]
 * @property {Language} language
 * the language of this component, some default text would be influenced.
 * @property {Object} style
 * the in-line css style of children element.
 */


import React from 'react';
import {CheckboxGroup, Flex, FlexItem, Input, RadioGroup} from "@chatui/core";
import './baseComposer.css';
import Integer from "./integer";
import ImageInput from "./image";
import Time from './time'
import DateComposer from "./date";
import Float from "./float";
import ProductsInput from "./goods";
import StoreStatus from "./storeStatus";

/**
 *
 * @param {BaseComposerProps} props
 * @returns {JSX.Element}
 * @constructor
 */
function BaseComposer(props) {

    const {
        name,
        type,
        value,
        setValue,
        format,
        className,
        description,
        children,
        style,
        language
    } = props;

    const classNames = `${className} ${type}`;

    // 渲染字段标签和描述
    const renderFieldInfo = () => (
        <>
            <div className="field-name">{name}</div>
            <div className="field-description">{description}</div>
        </>
    );

    // 根据类型渲染不同的输入组件
    const renderInputComponent = () => {
        switch (type) {
            case "radio":
                return (
                    <RadioGroup
                        className={classNames}
                        options={format?.options}
                        value={value}
                        onChange={(val) => setValue(val)}
                    />
                );

            case "int":
                return (
                    <Integer
                        className={classNames}
                        value={value}
                        setValue={setValue}
                        maximum={format?.maximum}
                        minimum={format?.minimum}
                        editable={format?.editable}
                    />
                );

            case "float":
                return (
                    <Float
                        className={classNames}
                        value={value}
                        setValue={setValue}
                    />
                );
            case "textarea":
                return (
                    <Input
                        className={classNames}
                        value={value}
                        onChange={(val)=>setValue(val)}
                        minRows={5}
                        maxRows={10}
                        autoSize
                    />
                )

            case "image":
                return (
                    <ImageInput
                        className={classNames}
                        images={value}
                        setImages={setValue}
                    />
                )
            case "time":
                return (
                    <Time
                        className={classNames}
                        time={value}
                        setTime={setValue}
                    />
                )
            case "date":
                return (
                    <DateComposer
                        className={classNames}
                        date={value}
                        setDate={setValue}
                    />
                )

            case "products":
                return (
                    <ProductsInput
                        className={classNames}
                        currency={format?.currency}
                        goodsList={value}
                        setGoodsList={setValue}
                        language={language}
                        defaultText={language==="zh_cn"? "还没有选择任何商品": "There is no selected products"}
                        buttonText={language==="zh_cn"? "选择": "Select"}
                    />
                )

            case "checkbox":
                return (
                    <CheckboxGroup options={format?.options} value={value} onChange={(val)=>setValue(val)}/>
                );

            case 'store-status':
                return (
                    <StoreStatus
                        value={value}
                        setValue={setValue}
                        language={language}
                        optionList={format?.options}
                    ></StoreStatus>
                )

            // 处理所有使用普通 Input 的类型
            case "address":
            case "order":
            case "text":
                return (
                    <Input
                        className={classNames}
                        value={value}
                        onChange={(val) => setValue(val)}
                    />
                );

            default:
                return (
                    <Input
                        className={classNames}
                        value={value}
                        onChange={(val) => setValue(val)}
                    />
                );
        }
    };

    // 特殊布局处理
    if (type === "int" || type === 'time' || type === 'date' || type === 'float') {
        return (
            <div className={`${type}-composer composer-container`}>
                <Flex center>
                    <FlexItem>
                        {renderFieldInfo()}
                    </FlexItem>
                    {renderInputComponent()}
                </Flex>
            </div>
        );
    }

    if (type === 'default')
        return (
            <div className={`${type}-composer composer-container`}>
                {renderFieldInfo()}
                <div style={style}>
                    {children}
                </div>
            </div>
        )

    // 通用布局
    return (
        <div className={`${type}-composer composer-container`}>
            {renderFieldInfo()}
            {renderInputComponent()}
        </div>
    );
}

export default BaseComposer;